/**
 * FootballActionController (§2) — the shared action-phase state machine + the one
 * authoritative table of action timings. Every timed football action (pass, shot,
 * tackle, dribble touch, first touch) runs through these phases so the ball impulse
 * lands at the CONTACT frame instead of on the button tick.
 *
 * The per-action state lives on WorldState.players (actKind/actStartTick/actExecId/
 * actContacted + the release snapshot actAim/actPower/actCurl/actFoot/actTarget) so
 * it is deterministic, cloned and hashed like all sim state. The PHASE itself is
 * NOT stored — it is a pure function of (startTick, currentTick, def), so there is
 * one source of truth and nothing to keep in sync. Timings are normalized 0..1 over
 * the action's `durationTicks`, so a clip of any length maps onto the same phases.
 *
 * This module is pure data + pure functions; the impulse wiring is a later commit.
 */

export const enum ActionKind {
  None = 0,
  ShortPass = 1,
  ChargedPass = 2,
  NormalShot = 3,
  PowerShot = 4,
  FinesseShot = 5,
  StandingTackle = 6,
  PokeTackle = 7,
  SlideTackle = 8,
  DribbleTouch = 9,
  FirstTouch = 10,
  /**
   * A HEADER. Owner, live: "when players hits the ball with head its glitches
   * physics not working on it".
   *
   * Headers used to take the IMMEDIATE path — the impulse fired on the input
   * tick and `AnimState.Header` played afterwards, so the ball left before the
   * head arrived and the clip swung through empty air. That is the exact
   * defect contact timing exists to prevent; the old comment in ball.ts said
   * so and then exempted air actions as "out of scope this pass".
   */
  Header = 11,
}
export const ACTION_KIND_COUNT = 12;

export const enum ActionPhase {
  Idle = 0,
  Preparation = 1,
  Committed = 2,
  Contact = 3,
  FollowThrough = 4,
  Recovery = 5,
  Complete = 6,
  Cancelled = 7,
}

export const ACTION_PHASE_NAMES: readonly string[] = [
  'IDLE', 'PREPARATION', 'COMMITTED', 'CONTACT', 'FOLLOW_THROUGH', 'RECOVERY', 'COMPLETE', 'CANCELLED',
];
export const ACTION_KIND_NAMES: readonly string[] = [
  'none', 'short_pass', 'charged_pass', 'normal_shot', 'power_shot', 'finesse_shot',
  'standing_tackle', 'poke_tackle', 'slide_tackle', 'dribble_touch', 'first_touch',
];

/**
 * One action's shape. Phase boundaries are normalized 0..1 across `durationTicks`;
 * `contactWindowStart..End` is when a foot may reach the ball; the impulse fires on
 * the first tick inside that window where the socket is in range. `contactRange`
 * and `maxCorrection` are the §6 socket-to-ball limits.
 */
export interface FootballActionDef {
  readonly kind: ActionKind;
  readonly durationTicks: number;
  readonly preparationEnd: number;
  readonly commitTime: number;
  readonly contactWindowStart: number;
  readonly contactWindowEnd: number;
  readonly followThroughEnd: number;
  readonly recoveryEnd: number;
  /** Speed multiplier while the action runs (movement lock). 1 = free. */
  readonly movementLock: number;
  /** Rotation frozen until follow-through (a committed strike can't spin). */
  readonly rotationLock: boolean;
  /** Contact validation: socket-to-ball max for a hit (m). */
  readonly contactRange: number;
  /** Capped procedural correction when the foot narrowly misses (m). */
  readonly maxCorrection: number;
  /** Foot preference; 'auto' = choose from ball side / aim. */
  readonly preferFoot: 'auto' | 'left' | 'right';
  /** Interruptible only before commit (until then the input can be cancelled). */
  readonly interruptibleUntil: number;
}

function def(kind: ActionKind, o: Partial<FootballActionDef> & { durationTicks: number }): FootballActionDef {
  return {
    kind,
    preparationEnd: 0.3,
    commitTime: 0.3,
    contactWindowStart: 0.35,
    contactWindowEnd: 0.5,
    followThroughEnd: 0.75,
    recoveryEnd: 1,
    movementLock: 0.6,
    rotationLock: true,
    contactRange: 0.35,
    maxCorrection: 0.2,
    preferFoot: 'auto',
    interruptibleUntil: 0.3,
    ...o,
  };
}

/** The single timing table (§2 "do not scatter timings across files"). */
export const ACTION_DEFS: readonly FootballActionDef[] = (() => {
  const t: FootballActionDef[] = new Array(ACTION_KIND_COUNT);
  t[ActionKind.None] = def(ActionKind.None, { durationTicks: 1, contactWindowStart: 1, contactWindowEnd: 1 });
  // ShortPass was 20 ticks with the default 0.35–0.50 window, i.e. the impulse
  // fired 7 ticks (0.117 s) after the input — too fast for any clip to reach
  // the ball. Then it was 28 ticks (release 0.196 s), sized to a 1.6 s pass
  // clip that has since been replaced. Now 24 — THE SAME COMMITMENT AS
  // NormalShot (owner, 2026-08-14: "make the pass as quick as the kick") —
  // with the window fractions kept, so the impulse fires at 0.42 x 0.4 =
  // 0.168 s and the whole action releases the body 67 ms sooner. The bound
  // clip is the same snappy in-stride swing the light kick plays, its beat
  // recomputed to this release (see animationCommand.ts).
  // INSTANT (owner, 2026-08-15: "pass instant"). Measured at sprint: 0.35-
  // 0.45 s from button to ball - the buffer waiting for the dribbled ball to
  // roll back into a 0.35 m reach, then a 0.168 s window. Reach 0.6 accepts
  // the pass from a dribble's natural lead, window 0.15 releases at 0.06 s,
  // and the post-contact movementLock brake starts that much earlier too.
  // The follow-through still runs the full 24 ticks; the aim pivot finishes
  // during it (the ball already flies where it was aimed - impulse is from
  // aimYaw, never from the body).
  // ⚠ EVERY phase mark moves WITH the window (measured 2026-08-15: leaving
  // preparationEnd/commitTime/interruptibleUntil at their 0.3 defaults put
  // them AFTER the 0.15 contact - the pass was cancellable through its own
  // strike, and in-game held inputs swallowed it entirely: "pass is not
  // happening"). An instant action commits instantly.
  t[ActionKind.ShortPass] = def(ActionKind.ShortPass, { durationTicks: 24, preparationEnd: 0.08, commitTime: 0.08, interruptibleUntil: 0.08, contactWindowStart: 0.15, contactWindowEnd: 0.55, contactRange: 0.6, maxCorrection: 0.3 });
  t[ActionKind.ChargedPass] = def(ActionKind.ChargedPass, { durationTicks: 24, contactRange: 0.35, maxCorrection: 0.2 });
  t[ActionKind.NormalShot] = def(ActionKind.NormalShot, { durationTicks: 24, contactRange: 0.35, maxCorrection: 0.2 });
  t[ActionKind.PowerShot] = def(ActionKind.PowerShot, { durationTicks: 28, contactWindowStart: 0.4, contactWindowEnd: 0.55, contactRange: 0.35, maxCorrection: 0.2 });
  t[ActionKind.FinesseShot] = def(ActionKind.FinesseShot, { durationTicks: 26, contactRange: 0.35, maxCorrection: 0.2 });
  // HeaderStrike is 24 f / 0.800 s with the head meeting the ball at our frame
  // 5 = 0.17 s, i.e. 21 % in — the clip was spliced to put contact there (see
  // tools/anim/retarget/splice_control.py "header"). The window brackets that.
  // contactRange is generous because a header is a whole-body movement into the
  // ball, not a boot placed against it.
  t[ActionKind.Header] = def(ActionKind.Header, { durationTicks: 24, contactWindowStart: 0.15, contactWindowEnd: 0.35, contactRange: 0.9, maxCorrection: 0.35 });
  t[ActionKind.StandingTackle] = def(ActionKind.StandingTackle, { durationTicks: 18, contactWindowStart: 0.25, contactWindowEnd: 0.45, contactRange: 0.45, maxCorrection: 0.25, movementLock: 0.35 });
  t[ActionKind.PokeTackle] = def(ActionKind.PokeTackle, { durationTicks: 14, contactWindowStart: 0.2, contactWindowEnd: 0.4, contactRange: 0.5, maxCorrection: 0.3, movementLock: 0.5 });
  t[ActionKind.SlideTackle] = def(ActionKind.SlideTackle, { durationTicks: 32, contactWindowStart: 0.2, contactWindowEnd: 0.5, followThroughEnd: 0.6, contactRange: 0.5, maxCorrection: 0.3, movementLock: 1, rotationLock: true });
  t[ActionKind.DribbleTouch] = def(ActionKind.DribbleTouch, { durationTicks: 8, contactWindowStart: 0.25, contactWindowEnd: 0.6, contactRange: 0.3, maxCorrection: 0.15, movementLock: 1, rotationLock: false });
  t[ActionKind.FirstTouch] = def(ActionKind.FirstTouch, { durationTicks: 12, contactWindowStart: 0.2, contactWindowEnd: 0.55, contactRange: 0.4, maxCorrection: 0.2, movementLock: 0.8, rotationLock: false });
  return t;
})();

export function actionDef(kind: ActionKind): FootballActionDef {
  return ACTION_DEFS[kind] ?? ACTION_DEFS[ActionKind.None]!;
}

/** Normalized action time 0..1 (clamped) for an action started at `startTick`. */
export function normalizedActionTime(startTick: number, currentTick: number, def: FootballActionDef): number {
  if (def.durationTicks <= 0) return 1;
  const t = (currentTick - startTick) / def.durationTicks;
  return t < 0 ? 0 : t > 1 ? 1 : t;
}

/** The phase of an action from its elapsed normalized time (pure). */
export function actionPhaseAt(startTick: number, currentTick: number, def: FootballActionDef): ActionPhase {
  const n = normalizedActionTime(startTick, currentTick, def);
  if (currentTick - startTick >= def.durationTicks) return ActionPhase.Complete;
  if (n < def.preparationEnd) return ActionPhase.Preparation;
  if (n < def.contactWindowStart) return ActionPhase.Committed;
  if (n <= def.contactWindowEnd) return ActionPhase.Contact;
  if (n < def.followThroughEnd) return ActionPhase.FollowThrough;
  return ActionPhase.Recovery;
}

/** True while the contact window is open (a foot may reach the ball this tick). */
export function inContactWindow(startTick: number, currentTick: number, def: FootballActionDef): boolean {
  const n = normalizedActionTime(startTick, currentTick, def);
  return n >= def.contactWindowStart && n <= def.contactWindowEnd
    && currentTick - startTick < def.durationTicks;
}

/** A challenge action (standing/poke/slide). Unlike a footed strike it self-gates
 *  on its own reach envelope (resolveTackle's range + ball-first geometry), so it
 *  fires on entering the contact window rather than on a socket-to-ball proximity
 *  test — a mistimed challenge must still be able to FOUL, not silently whiff. */
export function isTackleKind(kind: ActionKind): boolean {
  return kind === ActionKind.StandingTackle
    || kind === ActionKind.PokeTackle
    || kind === ActionKind.SlideTackle;
}

/** A low-priority BALL TOUCH (dribble carry / first touch). These re-arm
 *  continuously while a player carries the ball, so a deliberate strike is
 *  allowed to PREEMPT one — otherwise a shot taken while moving finds a touch in
 *  flight and falls off the contact-timed path onto the immediate one. */
export function isTouchKind(kind: ActionKind): boolean {
  return kind === ActionKind.DribbleTouch || kind === ActionKind.FirstTouch;
}
